import React, { useState } from 'react';
import axios from 'axios';
import { CalendarCheck, CheckCircle2, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { API } from '../lib/shop';

const SLOTS = [
  { value: 'morning', label: 'Morning (8am – 12pm)' },
  { value: 'afternoon', label: 'Afternoon (12pm – 4pm)' },
  { value: 'evening', label: 'Evening (4pm – 7pm)' },
];
const EMPTY = { name: '', phone: '', email: '', address: '', preferred_date: '', time_slot: '', notes: '' };

const tomorrow = () => {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  return d.toISOString().slice(0, 10);
};

const Field = ({ label, children }) => (
  <label className="block">
    <span className="text-sm font-medium text-gray-700 mb-1 block">{label}</span>
    {children}
  </label>
);

export const SurveyDialog = ({ open, onOpenChange }) => {
  const [form, setForm] = useState(EMPTY);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);

  const set = (e) => setForm(f => ({ ...f, [e.target.name]: e.target.value }));

  const close = (v) => {
    onOpenChange(v);
    if (!v) setTimeout(() => { setDone(null); setForm(EMPTY); }, 300);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    if (!form.time_slot) { toast.error('Please choose a preferred time'); return; }
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/surveys/book`, form);
      setDone(data);
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Could not send your booking. Please check the details and try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="max-w-lg max-h-[92vh] overflow-y-auto rounded-2xl" data-testid="survey-dialog">
        {done ? (
          <div className="text-center py-4" data-testid="survey-success">
            <CheckCircle2 className="w-14 h-14 text-green-500 mx-auto mb-4" />
            <DialogTitle className="text-2xl font-bold text-gray-900 mb-2">Survey request received</DialogTitle>
            <DialogDescription className="text-gray-600 mb-4">
              Thanks {done.name.split(' ')[0]}! We'll email you at <strong>{done.email}</strong> to confirm your free home survey on{' '}
              <strong>{new Date(done.preferred_date).toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long' })}</strong>, {done.time_slot_label.toLowerCase()}.
            </DialogDescription>
            <p className="text-xs text-gray-500 mb-6">Reference: <span data-testid="survey-reference">{done.id.slice(0, 8).toUpperCase()}</span></p>
            <Button onClick={() => close(false)} className="bg-pink-500 hover:bg-pink-600 text-white rounded-full" data-testid="survey-close">Done</Button>
          </div>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-2xl font-bold text-gray-900"><CalendarCheck className="w-6 h-6 text-pink-600" /> Book a free home survey</DialogTitle>
              <DialogDescription>We'll visit your home, take a look around and discuss the services you need. No obligation.</DialogDescription>
            </DialogHeader>
            <form onSubmit={submit} className="space-y-4 mt-2" data-testid="survey-form">
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Full name *"><Input name="name" value={form.name} onChange={set} required minLength={2} data-testid="survey-name" /></Field>
                <Field label="Phone *"><Input type="tel" name="phone" value={form.phone} onChange={set} required minLength={6} data-testid="survey-phone" /></Field>
              </div>
              <Field label="Email *"><Input type="email" name="email" value={form.email} onChange={set} required data-testid="survey-email" /></Field>
              <Field label="Property address *"><Input name="address" value={form.address} onChange={set} required minLength={5} placeholder="Street, town, postcode" data-testid="survey-address" /></Field>
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Preferred date *"><Input type="date" name="preferred_date" value={form.preferred_date} onChange={set} required min={tomorrow()} data-testid="survey-date" /></Field>
                <Field label="Preferred time *">
                  <select name="time_slot" value={form.time_slot} onChange={set} required data-testid="survey-time-slot" className="w-full h-10 px-3 border border-gray-300 rounded-md bg-white text-sm focus:outline-none focus:ring-2 focus:ring-pink-500">
                    <option value="">Choose a time</option>
                    {SLOTS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                  </select>
                </Field>
              </div>
              <Field label="Anything we should know? (optional)"><Textarea name="notes" value={form.notes} onChange={set} rows={3} placeholder="e.g. 3-bed house, interested in a weekly clean and oven cleaning" data-testid="survey-notes" /></Field>
              <Button type="submit" disabled={submitting} className="w-full bg-pink-500 hover:bg-pink-600 text-white rounded-full h-12" data-testid="survey-submit">
                {submitting ? <Loader2 className="w-5 h-5 mr-2 animate-spin" /> : <CalendarCheck className="w-5 h-5 mr-2" />}
                {submitting ? 'Sending...' : 'Book my free survey'}
              </Button>
              <p className="text-xs text-gray-500 text-center">We'll confirm the exact time by email or phone.</p>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
